import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { makeStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import Legion00 from '../images/legion-emblems/0-0.png';
import Legion01 from '../images/legion-emblems/0-1.png';
import Legion02 from '../images/legion-emblems/0-2.png';
import Legion03 from '../images/legion-emblems/0-3.png';
import Legion04 from '../images/legion-emblems/0-4.png';
import Legion10 from '../images/legion-emblems/1-0.png';
import Legion11 from '../images/legion-emblems/1-1.png';
import Legion12 from '../images/legion-emblems/1-2.png';
import Legion13 from '../images/legion-emblems/1-3.png';
import Legion14 from '../images/legion-emblems/1-4.png';
import Legion20 from '../images/legion-emblems/2-0.png';
import Legion21 from '../images/legion-emblems/2-1.png';
import Legion22 from '../images/legion-emblems/2-2.png';
import Legion23 from '../images/legion-emblems/2-3.png';
import Legion24 from '../images/legion-emblems/2-4.png';
import Legion30 from '../images/legion-emblems/3-0.png';
import Legion31 from '../images/legion-emblems/3-1.png';
import Legion32 from '../images/legion-emblems/3-2.png';
import Legion33 from '../images/legion-emblems/3-3.png';
import Legion34 from '../images/legion-emblems/3-4.png';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        legionEmblem: {
            height: 140,
            paddingBottom: 10,
            width: 'inherit',
            textAlign: 'center',
            verticalAlign: 'middle',
            display: 'flex',
        },
        legionEmblemImage: {
            height: 'auto',
            width: 120,
            margin: 'auto',
        },
        legionRankName: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: '#555',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '1rem',
            textTransform: 'uppercase',
            paddingBottom: 5,
        },
        legionRankLabel: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: '#555',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '0.7rem',
            lineHeight: '0.8rem',
            textTransform: 'uppercase',
            paddingTop: 4,
            marginBottom: 'none',
        },
        legionRankValue: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: '#555',
            textAlign: 'center',
            fontWeight: 300,
            fontSize: '1.1rem',
            lineHeight: '1.2rem',
            marginTop: 'none',
            paddingTop: 'none',
            paddingBottom: 4,
        },
        legionDivider: {
            marginTop: '5px',
            marginBottom: '5px',
        },
        noLegionEmblem: {
            height: 140,
            paddingBottom: 10,
            width: 'inherit',
            textAlign: 'center',
            verticalAlign: 'middle',
            display: 'flex',
        },
        noLegionEmblemImage: {
            height: 'auto',
            width: 120,
            filter: 'grayscale(100%) contrast(50%) brightness(30%)',
            opacity: 0.3,
            margin: 'auto',
        },
        noLegionText: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: "#555",
            textAlign: 'center',
            fontWeight: 500,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
        },
    })
);

interface LegionInfoPanelProps {
    legionRank: number;
    legionLevel: number;
    legionPower: number;
    legionCoins: number;
    legionTimeToCap: number;
}

interface LegionDetails {
    LegionEmblemKey: string;
    LegionName: string;
    LegionCoinCap: number;
    LegionImage: string;
}

const determineLegionDetails = (level: number): LegionDetails => {
    let details: LegionDetails = {
        LegionEmblemKey: "",
        LegionName: "",
        LegionCoinCap: 0,
        LegionImage: Legion30,
    }

    if (level >= 1 && level <= 999) {
        details = { LegionEmblemKey: "0-0", LegionName: "Nameless Legion I", LegionCoinCap: 0, LegionImage: Legion00 };
    } else if (level >= 1000 && level <= 1499) {
        details = { LegionEmblemKey: "0-1", LegionName: "Nameless Legion II", LegionCoinCap: 0, LegionImage: Legion01 };
    } else if (level >= 1500 && level <= 1999) {
        details = { LegionEmblemKey: "0-2", LegionName: "Nameless Legion III", LegionCoinCap: 0, LegionImage: Legion02 };
    } else if (level >= 2000 && level <= 2499) {
        details = { LegionEmblemKey: "0-3", LegionName: "Nameless Legion IV", LegionCoinCap: 0, LegionImage: Legion03 };
    } else if (level >= 2500 && level <= 2999) {
        details = { LegionEmblemKey: "0-4", LegionName: "Nameless Legion V", LegionCoinCap: 0, LegionImage: Legion04 };
    } else if (level >= 3000 && level <= 3499) {
        details = { LegionEmblemKey: "1-0", LegionName: "Renowned Legion I", LegionCoinCap: 0, LegionImage: Legion10 };
    } else if (level >= 3500 && level <= 3999) {
        details = { LegionEmblemKey: "1-1", LegionName: "Renowned Legion II", LegionCoinCap: 0, LegionImage: Legion11 };
    } else if (level >= 4000 && level <= 4499) {
        details = { LegionEmblemKey: "1-2", LegionName: "Renowned Legion III", LegionCoinCap: 0, LegionImage: Legion12 };
    } else if (level >= 4500 && level <= 4999) {
        details = { LegionEmblemKey: "1-3", LegionName: "Renowned Legion IV", LegionCoinCap: 0, LegionImage: Legion13 };
    } else if (level >= 5000 && level <= 5499) {
        details = { LegionEmblemKey: "1-4", LegionName: "Renowned Legion V", LegionCoinCap: 0, LegionImage: Legion14 };
    } else if (level >= 5500 && level <= 5999) {
        details = { LegionEmblemKey: "2-0", LegionName: "Heroic Legion I", LegionCoinCap: 0, LegionImage: Legion20 };
    } else if (level >= 6000 && level <= 6499) {
        details = { LegionEmblemKey: "2-1", LegionName: "Heroic Legion II", LegionCoinCap: 0, LegionImage: Legion21 };
    } else if (level >= 6500 && level <= 6999) {
        details = { LegionEmblemKey: "2-2", LegionName: "Heroic Legion III", LegionCoinCap: 0, LegionImage: Legion22 };
    } else if (level >= 7000 && level <= 7499) {
        details = { LegionEmblemKey: "2-3", LegionName: "Heroic Legion IV", LegionCoinCap: 0, LegionImage: Legion23 };
    } else if (level >= 7500 && level <= 7999) {
        details = { LegionEmblemKey: "2-4", LegionName: "Heroic Legion V", LegionCoinCap: 0, LegionImage: Legion24 };
    } else if (level >= 8000 && level <= 8499) {
        details = { LegionEmblemKey: "3-0", LegionName: "Legendary Legion I", LegionCoinCap: 0, LegionImage: Legion30 };
    } else if (level >= 8500 && level <= 8999) {
        details = { LegionEmblemKey: "3-1", LegionName: "Legendary Legion II", LegionCoinCap: 0, LegionImage: Legion31 };
    } else if (level >= 9000 && level <= 9499) {
        details = { LegionEmblemKey: "3-2", LegionName: "Legendary Legion III", LegionCoinCap: 0, LegionImage: Legion32 };
    } else if (level >= 9500 && level <= 9999) {
        details = { LegionEmblemKey: "3-3", LegionName: "Legendary Legion IV", LegionCoinCap: 0, LegionImage: Legion33 };
    } else if (level >= 10000) {
        details = { LegionEmblemKey: "3-4", LegionName: "Legendary Legion V", LegionCoinCap: 0, LegionImage: Legion34 };
    }

    if (level < 3000) {
        details.LegionCoinCap = 200;
    } else if (level < 5500) {
        details.LegionCoinCap = 300;
    } else if (level < 8000) {
        details.LegionCoinCap = 500;
    } else if (level <= 15000) {
        details.LegionCoinCap = 700;
    }

    return details;
}

interface LegionCoinDetails {
    LegionCoinsPerDay: number;
    TimeToCapCoins: string;
    CoinCapSeconds: number;
}

const legionCoinDetails = (legionPower: number, legionCoinCap: number): LegionCoinDetails => {
    let details: LegionCoinDetails = {
        LegionCoinsPerDay: 0,
        TimeToCapCoins: "",
        CoinCapSeconds: 0,
    }

    if (legionPower >= 1 && legionCoinCap > 0) {
        let secsPerCoin: number = 100000000000 / legionPower;
        let secsPerDay: number = 86400;

        let totalCoinsPerDay = Math.round(secsPerDay / secsPerCoin);
        details.LegionCoinsPerDay = totalCoinsPerDay;

        // var coinCapSeconds = Math.round(this.state.LegionCoinCap / (secsPerDay / secsPerCoin) * secsPerDay);
        var coinCapSeconds = (legionCoinCap / totalCoinsPerDay) * secsPerDay;

        var days = Math.floor(coinCapSeconds / secsPerDay);
        coinCapSeconds %= secsPerDay;
        let hours = Math.floor(coinCapSeconds / 3600).toString(10);
        if (hours.length === 1) {
            hours = "0" + hours;
        }
        coinCapSeconds %= 3600;

        var minutes = Math.floor(coinCapSeconds / 60).toString(10);
        if (minutes.length === 1) {
            minutes = "0" + minutes;
        }

        var seconds = Math.ceil(coinCapSeconds % 60).toString(10);
        if (seconds.length === 1) {
            seconds = "0" + seconds;
        }

        var timeToCapStr = hours + " : " + minutes + " : " + seconds;
        if (days > 0) {
            timeToCapStr = days + " : " + timeToCapStr;
        }

        details.TimeToCapCoins = timeToCapStr;
        details.CoinCapSeconds = coinCapSeconds;
    }

    return details
}

const LegionInfoPanel: FC<LegionInfoPanelProps> = (props: LegionInfoPanelProps) => {
    const classes = useStyles();

    if (props.legionRank === 0) {
        return (
            <Fragment>
                <div className={classes.noLegionEmblem}>
                    <img className={classes.noLegionEmblemImage} src={Legion30} alt="Legion emblem placeholder" />
                </div>
                <Typography variant="subtitle1" className={classes.noLegionText}>No legion information available</Typography>
            </Fragment>
        );
    }

    const legionDetails = determineLegionDetails(props.legionLevel);
    const coinDetails = legionCoinDetails(props.legionPower, legionDetails.LegionCoinCap);
    return (
        <Fragment>
            <div className={classes.legionEmblem}>
                <img className={classes.legionEmblemImage} alt={`${legionDetails.LegionName} emblem`} src={legionDetails.LegionImage} />
            </div>

            <Typography variant="subtitle1" className={classes.legionRankName}>{legionDetails.LegionName}</Typography>
            <Divider className={classes.legionDivider} />
            <Typography variant="subtitle1" component="p" className={classes.legionRankLabel}>Legion Rank</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankValue}>{props.legionRank.toLocaleString("en-US", { useGrouping: true })}</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankLabel}>Legion Level</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankValue}>{props.legionLevel.toLocaleString("en-US", { useGrouping: true })}</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankLabel}>Legion Power</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankValue}>{props.legionPower.toLocaleString("en-US", { useGrouping: true })}</Typography>
            <Divider className={classes.legionDivider} />
            <Typography variant="subtitle1" component="p" className={classes.legionRankLabel}>Coins Per Day</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankValue}>{props.legionCoins.toLocaleString("en-US", { useGrouping: true })}</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankLabel}>Time Until Capped</Typography>
            <Typography variant="subtitle1" component="p" className={classes.legionRankValue}>{coinDetails.TimeToCapCoins}</Typography>
        </Fragment>
    )
}

export default LegionInfoPanel;
